import { useState, useCallback } from 'react';
import type { ChangeEvent, MouseEvent, FormEvent } from 'react';
import { z } from 'zod';
import { validateEmail, validateMobileIndia } from '@/shared/utils/validation';
import { buildStoreGiftCard } from '../../config/storeGiftCard.build';
import { useStoreGiftCard } from './useStoreGiftCard';
import type { GiftCardFormData } from '../../domain/models/storeGiftCard.types';

const storeGiftCardService = buildStoreGiftCard().service;

const storeGiftCardSchema = z
  .object({
    amount: z
      .union([z.number(), z.string()])
      .refine((val) => Number(val) >= 1000 && Number(val) <= 50000, {
        message: 'Amount must be between ₹1,000 and ₹50,000.',
      }),
    rfname: z.string().trim().min(1, "Please enter recipient's name."),
    rmobile: z.string().trim().refine((val) => validateMobileIndia(val) === null, {
      message: 'Please enter a valid 10-digit mobile number.',
    }),
    remail: z.string().trim().refine((val) => validateEmail(val) === null, {
      message: 'Please enter a valid email address.',
    }),
    cremail: z.string().trim().refine((val) => validateEmail(val) === null, {
      message: 'Please enter a valid confirm email address.',
    }),
    pincode: z
      .string()
      .trim()
      .regex(/^[1-9][0-9]{5}$/, 'Please enter a valid pincode.'),
    sfname: z.string().trim().min(1, "Please enter sender's name."),
  })
  .refine((data) => data.remail.toLowerCase() === data.cremail.toLowerCase(), {
    message: 'Recipient email and confirm email must match.',
    path: ['cremail'],
  });

export type FormFieldKeys = keyof GiftCardFormData;
export type FieldErrorsMap = Partial<Record<FormFieldKeys, string>>;

export interface FormFieldConfig {
  name: FormFieldKeys;
  label: string;
  type: string;
  maxLength?: number;
}

export const RECIPIENT_FORM_FIELDS: FormFieldConfig[] = [
  { name: 'rfname', label: "Recipient's Name", type: 'text' },
  { name: 'rmobile', label: "Recipient's Mobile", type: 'tel', maxLength: 10 },
  { name: 'remail', label: "Recipient's Email", type: 'email' },
  { name: 'cremail', label: "Confirm Recipient's Email", type: 'email' },
  { name: 'sfname', label: "Sender's Name", type: 'text' },
];

export const useGiftCardForm = () => {
  const storeGc = useStoreGiftCard(storeGiftCardService);

  const [formData, setFormData] = useState<GiftCardFormData>({
    amount: '',
    rfname: '',
    rmobile: '',
    remail: '',
    cremail: '',
    pincode: '',
    sfname: '',
  });

  const [fieldErrors, setFieldErrors] = useState<FieldErrorsMap>({});

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const fieldName = name as FormFieldKeys;

      setFieldErrors((prev) => (prev[fieldName] ? { ...prev, [fieldName]: undefined } : prev));

      if (fieldName === 'pincode') {
        storeGc.resetPincodeState();
      }

      setFormData((prev) => ({
        ...prev,
        [fieldName]: fieldName === 'amount' ? (value ? Number(value) : '') : value,
      }));
    },
    [storeGc]
  );

  const handleAmountSelect = useCallback((val: number) => {
    setFieldErrors((prev) => (prev.amount ? { ...prev, amount: undefined } : prev));
    setFormData((prev) => ({ ...prev, amount: val }));
  }, []);

  const handlePincodeCheckClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (!/^[1-9][0-9]{5}$/.test(formData.pincode.trim())) {
        setFieldErrors((prev) => ({ ...prev, pincode: 'Please enter a valid pincode.' }));
        return;
      }
      storeGc.checkPincodeManual(formData.pincode.trim());
    },
    [formData.pincode, storeGc]
  );

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setFieldErrors({});

      const result = storeGiftCardSchema.safeParse(formData);

      if (!result.success) {
        const flattenedErrors = result.error.flatten().fieldErrors;
        const formattedErrors: FieldErrorsMap = {};

        Object.keys(flattenedErrors).forEach((key) => {
          const errorArray = flattenedErrors[key as FormFieldKeys];
          if (errorArray && errorArray.length > 0) {
            formattedErrors[key as FormFieldKeys] = errorArray[0];
          }
        });

        setFieldErrors(formattedErrors);
        return;
      }

      storeGc.processOrder(formData);
    },
    [formData, storeGc]
  );

  return {
    ...storeGc,
    formData,
    fieldErrors,
    handleChange,
    handleAmountSelect,
    handlePincodeCheckClick,
    handleSubmit,
  };
};